import React from 'react';
import blogPosts from './blogPosts.json';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Reveal from './Reveal';

const HEADINGS = {
    en: { title: 'From the Blog', subtitle: 'Latest articles & practical nutrition tips' },
    ar: { title: 'المدونة', subtitle: 'أحدث المقالات والنصائح العملية' },
    tr: { title: 'Blog', subtitle: 'Son yazılar ve pratik beslenme ipuçları' },
};

const BlogSection = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    // Blog content exists in EN/AR; Turkish falls back to English
    const contentLang = i18n.language === 'ar' ? 'ar' : 'en';
    const heading = HEADINGS[i18n.language] || HEADINGS.en;

    const openPost = (postId) => navigate(`/blog/${postId}`);

    const formatDate = (iso) =>
        new Date(iso).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

    const [featured, ...rest] = blogPosts;

    const Card = ({ post, wide = false, delay = 0 }) => {
        const copy = post[contentLang];
        return (
            <Reveal
                as="article"
                delay={delay}
                className={wide ? 'sm:col-span-2' : ''}
            >
                <button
                    type="button"
                    onClick={() => openPost(post.id)}
                    className={`sk-card sk-card--hover group w-full h-full text-start cursor-pointer overflow-hidden p-0 flex ${
                        wide ? 'flex-col sm:flex-row' : 'flex-col'
                    }`}
                >
                    <div className={`overflow-hidden bg-primary-soft ${wide ? 'sm:w-[46%] sm:flex-shrink-0' : ''}`}>
                        <img
                            src={post.image.startsWith('http') ? post.image : `/${post.image}`}
                            alt=""
                            loading="lazy"
                            width="480"
                            height="300"
                            className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${
                                wide ? 'h-52 sm:h-full' : 'h-44'
                            }`}
                        />
                    </div>
                    <div className="flex flex-col gap-2.5 p-5 sm:p-6">
                        <span className="text-[0.75rem] font-semibold uppercase tracking-wider text-ink-soft">
                            {formatDate(post.date)} · {post.author}
                        </span>
                        <h3 className={`m-0 font-heading font-semibold text-primary-deep leading-snug transition-colors duration-200 group-hover:text-primary ${
                            wide ? 'text-[1.25rem]' : 'text-[1.05rem]'
                        }`}>
                            {copy.title}
                        </h3>
                        <p className="m-0 text-ink-soft text-[0.92rem] leading-relaxed line-clamp-2">
                            {copy.subtitle}
                        </p>
                        <span className="mt-auto pt-1 inline-flex items-center gap-1.5 text-primary font-heading font-semibold text-[0.85rem]">
                            {contentLang === 'ar' ? 'اقرأ المقال' : 'Read article'}
                            <i className="fas fa-arrow-right rtl:rotate-180 text-[0.7rem] transition-transform duration-200 group-hover:translate-x-1 rtl:group-hover:-translate-x-1" aria-hidden="true" />
                        </span>
                    </div>
                </button>
            </Reveal>
        );
    };

    return (
        <section id="blog" className="relative bg-mist py-16 md:py-24 border-t border-line">
            <div className="sk-container">
                <Reveal className="max-w-[640px] mb-12">
                    <p className="sk-eyebrow">{heading.title}</p>
                    <h2 className="mt-3 font-heading font-bold text-primary-deep text-[clamp(1.6rem,3.5vw,2.4rem)] leading-tight">
                        {heading.subtitle}
                    </h2>
                </Reveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    <Card post={featured} wide />
                    {rest.map((post, i) => (
                        <Card key={post.id} post={post} delay={(i % 3) * 70} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BlogSection;
